import { useContext } from "react";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import UserContext from "common/Context/UserContext";
import { getProfileDisplayRoles } from "common/Utilities/Role.utils";
import { useEffectiveAccess } from "common/Context/EffectiveAccessContext";
import { EFFECTIVE_ACCESS_PAGE_KEY } from "common/Constants/EffectiveAccess.constants";
import { resolveDefaultCustomerAdminPath, shouldShowCustomerSwitcher } from "common/Utilities/EffectiveAccess.utils";
import { useResetPasswordFlow } from "pages/Authentication/ConfirmResetPassword/useResetPasswordFlow";
import {
  isPlatformAdminProfile
} from "common/Utilities/Role.utils";
import NavCollapseButton from "./Components/NavCollapseButton";
import HeaderLogo from "./Components/HeaderLogo";
import CustomerSwitcher from "./Components/CustomerSwitcher";
import HelpMenu from "./Components/HelpMenu";
import PlatformHomeButton from "./Components/PlatformHomeButton";
import UserAccountMenu from "./Components/UserAccountMenu";
import { handleHeaderMouseDownCapture } from "./useHeaderMenuAnchor";
import {
  headerBarSx,
  headerDividerSx,
  headerLeftSx,
  headerRightSx,
  HeaderActionsGroup,
  headerSpacerSx,
} from "./TopHeader.style";

const TopHeader = ({ isNavCollapsed, onToggleNav, showSideNav = true }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userProfile, logout } = useContext(UserContext);
  const { canAccessPage, usesEffectiveAccess } = useEffectiveAccess();
  const { requestResetPassword, resetPasswordAlert } = useResetPasswordFlow();

  const profile = userProfile?.profile ?? {};
  const displayRoles = getProfileDisplayRoles(profile) || [];
  const showAdministration =
    usesEffectiveAccess &&
    (canAccessPage(EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT) ||
      canAccessPage(EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT));
  const showCustomerSwitcher = shouldShowCustomerSwitcher(profile);

  const handleAdministration = () => {
    navigate(resolveDefaultCustomerAdminPath(profile));
  };
  const showHomeButton = isPlatformAdminProfile(profile) && pathname !== "/customers" && pathname !== "/user-access";


  return (
    <>
      <Box
        component="header"
        sx={headerBarSx}
        onMouseDownCapture={handleHeaderMouseDownCapture}
      >
        <Box sx={headerLeftSx}>
          {showSideNav ? (
            <NavCollapseButton
              isCollapsed={isNavCollapsed}
              onToggle={onToggleNav}
            />
          ) : null}
        </Box>
        <HeaderLogo />
        <Box sx={headerSpacerSx} />
        <Box sx={headerRightSx}>
          {showCustomerSwitcher ? (
            <>
              <CustomerSwitcher />
              <Box sx={headerDividerSx} />
            </>
          ) : null}
          <HeaderActionsGroup>
            <HelpMenu />
            {showHomeButton &&
            <PlatformHomeButton />
}
          </HeaderActionsGroup>
          <UserAccountMenu
            name={profile.name || profile.id || ""}
            email={(profile.email || "").toLowerCase()}
            phone={profile.mobile_number || ""}
            roles={displayRoles}
            showAdministration={showAdministration}
            onLogout={logout}
            onResetPassword={requestResetPassword}
            onAdministration={handleAdministration}
          />
        </Box>
      </Box>
      {resetPasswordAlert}
    </>
  );
};

TopHeader.propTypes = {
  isNavCollapsed: PropTypes.bool.isRequired,
  onToggleNav: PropTypes.func.isRequired,
  showSideNav: PropTypes.bool,
};

export default TopHeader;
